import { useAuth } from "@clerk/clerk-react";
import React, { createContext, useContext, useState } from "react";
import { ChatMessage } from "schemas/dashboard";

import {
  FormState,
  STEP_LABELS,
  StepKey,
  StepState,
  StepStatus,
  buildPayload,
  initialFormState,
  stepsForForm,
} from "features/Influencer/QuickSendIndividualDrawer/Dialogs/PersonalizeDialog/schema";
import { fetcherAuth } from "utils/api";
import { useSubscription } from "utils/useSubscription";

import { AlertContext } from "./Alert";
import { OrganizationUserContext } from "./Organization";
import { QuickSendContext } from "./QuickSend";
import { QuickSendDrawerContext } from "./QuickSendDrawer";

export type BaseSource = "editor" | "previous_ai";

interface ChatHelperContextInterface {
  chatMessages: ChatMessage[];
  fetchChatMessages: () => void;
  messageLoading: boolean;
  messageLimit: boolean;
  isTyping: boolean;
  steps: StepState[];
  runPersonalize: (
    formOverride: Partial<FormState>,
    options?: { userMessage?: string; baseSource?: BaseSource },
  ) => Promise<void>;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface: ChatHelperContextInterface = {
  chatMessages: [],
  fetchChatMessages: defaultContextMissingFunction,
  messageLoading: false,
  messageLimit: false,
  isTyping: false,
  steps: [],
  runPersonalize: async () => {
    defaultContextMissingFunction();
  },
};

const ChatHelperContext =
  createContext<ChatHelperContextInterface>(defaultInterface);

interface ChatHelperProviderProps {
  children: React.ReactNode;
}

type BrandCache = {
  messages: ChatMessage[];
  page: number;
  limit: boolean;
};

const EMPTY_CACHE: BrandCache = { messages: [], page: 1, limit: false };

const ChatHelperProvider = ({ children }: ChatHelperProviderProps) => {
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { contactName, outreachMessages } = useContext(QuickSendContext);
  const { quickSendBrandId } = useContext(QuickSendDrawerContext);
  const { decreaseChatCount } = useSubscription();

  const activeBrandId =
    quickSendBrandId && quickSendBrandId > 0 ? quickSendBrandId : null;

  const [cacheByBrand, setCacheByBrand] = useState<Record<number, BrandCache>>(
    {},
  );
  const [messageLoading, setMessageLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [steps, setSteps] = useState<StepState[]>([]);

  const activeCache: BrandCache = activeBrandId
    ? cacheByBrand[activeBrandId] ?? EMPTY_CACHE
    : EMPTY_CACHE;
  const chatMessages = activeCache.messages;

  const updateBrandCache = (
    brandId: number,
    updater: (prev: BrandCache) => BrandCache,
  ) => {
    setCacheByBrand((prev) => {
      const current = prev[brandId] ?? EMPTY_CACHE;
      return { ...prev, [brandId]: updater(current) };
    });
  };

  const fetchChatMessages = async () => {
    if (!currentOrg?.id || !activeBrandId) return;
    const brandId = activeBrandId;
    const cache = cacheByBrand[brandId] ?? EMPTY_CACHE;
    if (cache.limit) return;

    setMessageLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/chat-messages?page=${cache.page}&bento_brand_id=${brandId}`,
        "GET",
        {},
      );
      if (res.messages.length === 0) {
        updateBrandCache(brandId, (prev) => ({ ...prev, limit: true }));
        return;
      }
      updateBrandCache(brandId, (prev) => ({
        ...prev,
        messages: [...prev.messages, ...res.messages],
        page: res.page + 1,
      }));
    } catch (error) {
      setAlert(
        error?.message ||
          "Something went wrong. Please reach out to hello@onbento.com for assistance",
        "error",
      );
    } finally {
      setMessageLoading(false);
    }
  };

  const runPersonalize = async (
    formOverride: Partial<FormState>,
    options?: { userMessage?: string; baseSource?: BaseSource },
  ) => {
    if (!currentOrg?.id || !activeBrandId || isTyping) return;
    const brandId = activeBrandId;
    const baseSource: BaseSource = options?.baseSource ?? "editor";

    const previousAssistantHtml = (cacheByBrand[brandId]?.messages ?? []).find(
      (m) =>
        m.role === "assistant" &&
        !m.streaming &&
        !!m.message &&
        !!m.countsTowardQuota,
    )?.message;
    const draftHtml =
      baseSource === "previous_ai" && previousAssistantHtml
        ? previousAssistantHtml
        : outreachMessages[0]?.body ?? "";

    const form: FormState = { ...initialFormState, ...formOverride };
    const payload = buildPayload(
      form,
      brandId,
      draftHtml,
      contactName ?? undefined,
      "chat_helper",
      baseSource,
    );

    const userMessage = options?.userMessage ?? buildSyntheticUserMessage(form);

    const plannedSteps = stepsForForm(form);
    setSteps(
      plannedSteps.map((key) => ({
        key,
        label: STEP_LABELS[key],
        status: "pending" as StepStatus,
      })),
    );

    setIsTyping(true);
    const placeholderId = -Date.now();
    let hasPlaceholder = false;
    let target = "";

    updateBrandCache(brandId, (prev) => ({
      ...prev,
      messages: [
        {
          id: placeholderId - 1,
          role: "user",
          message: userMessage,
          countsTowardQuota: false,
          bentoBrandId: brandId,
        },
        ...prev.messages,
      ],
    }));

    try {
      const res: Response = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/recommendations/personalize`,
        "POST",
        {},
        payload,
        true,
      );

      if (!res || !res.ok || !res.body) {
        throw new Error("Something went wrong. Please reload and try again.");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";

        for (const evt of events) {
          const dataLine = evt
            .split("\n")
            .find((line) => line.startsWith("data:"));
          if (!dataLine) continue;
          let payloadEvent: {
            type: string;
            text?: string;
            html?: string;
            message?: string;
            step?: StepKey;
            status?: StepStatus;
          };
          try {
            payloadEvent = JSON.parse(dataLine.slice(5).trim());
          } catch {
            continue;
          }

          if (payloadEvent.type === "progress") {
            const stepKey = payloadEvent.step;
            const stepStatus = payloadEvent.status;
            if (stepKey && stepStatus) {
              setSteps((prev) =>
                prev.map((s) =>
                  s.key === stepKey
                    ? {
                        ...s,
                        status: stepStatus,
                        errorMessage: payloadEvent.message,
                      }
                    : s,
                ),
              );
            }
          } else if (payloadEvent.type === "delta") {
            target += payloadEvent.text ?? "";
            const text = target;
            if (!hasPlaceholder) {
              hasPlaceholder = true;
              updateBrandCache(brandId, (prev) => ({
                ...prev,
                messages: [
                  {
                    id: placeholderId,
                    role: "assistant",
                    message: text,
                    countsTowardQuota: false,
                    bentoBrandId: brandId,
                    streaming: true,
                  },
                  ...prev.messages,
                ],
              }));
            } else {
              updateBrandCache(brandId, (prev) => ({
                ...prev,
                messages: prev.messages.map((m) =>
                  m.id === placeholderId ? { ...m, message: text } : m,
                ),
              }));
            }
          } else if (payloadEvent.type === "done") {
            const finalHtml = payloadEvent.html ?? target;
            updateBrandCache(brandId, (prev) => ({
              ...prev,
              messages: prev.messages.map((m) =>
                m.id === placeholderId
                  ? {
                      ...m,
                      message: finalHtml,
                      streaming: false,
                      countsTowardQuota: true,
                    }
                  : m,
              ),
            }));
            decreaseChatCount();
          } else if (payloadEvent.type === "text_response") {
            const responseText =
              payloadEvent.message ||
              "I can only help you customize this outreach pitch.";
            if (hasPlaceholder) {
              updateBrandCache(brandId, (prev) => ({
                ...prev,
                messages: prev.messages.map((m) =>
                  m.id === placeholderId
                    ? {
                        ...m,
                        message: responseText,
                        streaming: false,
                        countsTowardQuota: false,
                      }
                    : m,
                ),
              }));
            } else {
              updateBrandCache(brandId, (prev) => ({
                ...prev,
                messages: [
                  {
                    id: placeholderId,
                    role: "assistant",
                    message: responseText,
                    countsTowardQuota: false,
                    bentoBrandId: brandId,
                  },
                  ...prev.messages,
                ],
              }));
            }
          } else if (payloadEvent.type === "error") {
            const message =
              payloadEvent.message || "Something went wrong. Please try again.";
            if (hasPlaceholder) {
              updateBrandCache(brandId, (prev) => ({
                ...prev,
                messages: prev.messages.filter((m) => m.id !== placeholderId),
              }));
            }
            setAlert(message, "error");
          }
        }
      }
    } catch (error) {
      if (hasPlaceholder) {
        updateBrandCache(brandId, (prev) => ({
          ...prev,
          messages: prev.messages.filter((m) => m.id !== placeholderId),
        }));
      }
      setAlert(
        error?.message ||
          "Something went wrong. Please reach out to hello@onbento.com for assistance",
        "error",
      );
    } finally {
      setIsTyping(false);
      setSteps([]);
    }
  };

  return (
    <ChatHelperContext.Provider
      value={{
        chatMessages,
        fetchChatMessages,
        messageLoading,
        messageLimit: activeCache.limit,
        isTyping,
        steps,
        runPersonalize,
      }}
    >
      {children}
    </ChatHelperContext.Provider>
  );
};

const PERSONALIZATION_LABELS: Array<[keyof FormState, string]> = [
  ["referenceInstagram", "Reference Instagram"],
  ["referenceProduct", "Reference product"],
  ["mentionCaseStudy", "Mention case study"],
  ["contentIdea", "Add content idea"],
  ["audienceAlignment", "Audience alignment"],
];

const TONE_LABELS: Record<string, string> = {
  make_shorter: "Make it shorter",
  add_detail: "Add more detail",
  warm_relatable: "More warm",
  enthusiastic_upbeat: "More enthusiastic",
  playful_witty: "More playful",
  professional_formal: "More professional",
  confident_bold: "More bold",
  brief_direct: "More direct",
};

function buildSyntheticUserMessage(form: FormState): string {
  const freeform = (form.freeform ?? "").trim();
  if (freeform) return freeform;

  const parts: string[] = [];
  for (const [key, label] of PERSONALIZATION_LABELS) {
    if (form[key]) parts.push(label);
  }
  if (form.tone) {
    parts.push(TONE_LABELS[form.tone] ?? form.tone);
  }
  return parts.length > 0 ? parts.join(", ") : "Customize this pitch";
}

export { ChatHelperProvider, ChatHelperContext };
